import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Footer from "../../components/Footer/Footer";
import {
  getPaymentOrderStatus,
  getPaymentOrderDetails,
  IMAGE_PATH,
} from "../../services/api";
import "./OrderSuccessPage.css";

const OrderSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const orderId = searchParams.get("order_id") || searchParams.get("orderId");

  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState("");
  const [error, setError] = useState("");
  const [orderData, setOrderData] = useState(null);

  useEffect(() => {
    async function verifyAndLoadOrder() {
      try {
        if (!orderId) {
          setError("Order ID missing");
          setLoading(false);
          return;
        }

        const [statusRes, detailRes] = await Promise.all([
          getPaymentOrderStatus(orderId),
          getPaymentOrderDetails(orderId),
        ]);

        const status = String(statusRes?.data?.status || "").toUpperCase();
        setPaymentStatus(status);

        if (detailRes?.data) {
          setOrderData(detailRes.data);
        }
      } catch (err) {
        console.error("Order success page error:", err);
        setError(
          err?.response?.data?.error ||
            err?.message ||
            "Unable to load order"
        );
      } finally {
        setLoading(false);
      }
    }

    verifyAndLoadOrder();
  }, [orderId]);

  const order = orderData?.order;
  const items = orderData?.items || [];
  const totals = orderData?.totals || {};

  const shipping = order?.shipping || {};
  const billing = order?.shipping || {};

  const customerName =
    shipping?.name || order?.customer?.name || "Customer";

  const subtotal = Number(totals?.subtotal || 0);
  const amountCharged = Number(order?.amount || totals?.amount_charged || 0);
  const difference = Number(totals?.difference || 0);

  const shippingValue = difference < 0 ? 0 : difference;
  const discountValue = difference < 0 ? Math.abs(difference) : 0;

  const formatMoney = (v) => `₹${Number(v || 0).toFixed(2)}`;

  // Build Google Maps embed URL from shipping address / pincode
  const shippingAddressLine = [shipping?.line1, shipping?.line2]
    .filter(Boolean)
    .join(", ");
  const shippingLocationLine = [
    shipping?.city,
    shipping?.state,
    shipping?.pincode,
    shipping?.country,
  ]
    .filter(Boolean)
    .join(", ");
  const mapQueryBase = shippingAddressLine || shippingLocationLine;
  const hasMapAddress = !!mapQueryBase;
  const mapSrc = hasMapAddress
    ? `https://www.google.com/maps?q=${encodeURIComponent(mapQueryBase)}&output=embed`
    : null;

  /* ---------------- IMAGE FIX ---------------- */

  const getProductImage = (item) => {
    const fallback = "/no-image.png";

    const parseImages = (val) => {
      if (!val) return [];
      if (Array.isArray(val)) return val;
      if (typeof val === "string") {
        const trimmed = val.trim();
        if (!trimmed) return [];
        if (trimmed.startsWith("[")) {
          try {
            const parsed = JSON.parse(trimmed);
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            return trimmed.includes(",") ? trimmed.split(",").map((s) => s.trim()).filter(Boolean) : [trimmed];
          }
        }
        return trimmed.includes(",") ? trimmed.split(",").map((s) => s.trim()).filter(Boolean) : [trimmed];
      }
      return [];
    };

    const raw = item?.images ?? item?.image ?? item?.product?.images ?? item?.product?.image;
    const list = parseImages(raw);
    if (list.length === 0) return fallback;

    const first = list[0];
    if (typeof first === "string") {
      if (first.startsWith("http")) return first;
      return `${IMAGE_PATH}${first}`;
    }
    if (first?.url) return first.url;
    if (first?.path) return `${IMAGE_PATH}${first.path}`;

    return fallback;
  };

  /* ---------------- LOADING ---------------- */

  if (loading) {
    return (
      <>
        <div className="success-page">
          <div className="success-shell">
            <div className="success-loading-card">
              <h2>Checking your order...</h2>
              <p>Please wait while we confirm your payment.</p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  /* ---------------- ERROR ---------------- */

  if (!orderData || error) {
    return (
      <>
        <div className="success-page">
          <div className="success-shell">
            <div className="success-loading-card">
              <h2>Unable to load order</h2>
              <p>{error || "Something went wrong."}</p>

              {orderId && (
                <p>
                  Order ID: <strong>{orderId}</strong>
                </p>
              )}

              <div className="success-top-actions">
                <button
                  className="success-action-btn dark"
                  onClick={() => navigate("/profile/orders")}
                >
                  View my orders
                </button>

                <button
                  className="success-action-btn light"
                  onClick={() => navigate("/products")}
                >
                  Continue shopping
                </button>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </>
    );
  }

  const isPaid =
    paymentStatus === "PAID" ||
    paymentStatus === "SUCCESS" ||
    paymentStatus === "ACTIVE";

  return (
    <>
      <div className="success-page">
        <div className="success-shell">

          {/* LEFT SIDE */}

          <div className="success-left">

            <div className="success-brand">
              AVNII
            </div>

            <div className="success-thankyou-row">

              <div className="success-check-icon">
                ✔
              </div>

              <div>
                <p className="success-order-no">
                  Order #{order?.local_order_id || orderId}
                </p>

                <h1>
                  {isPaid ? "Thank you" : "Order received"}, {customerName}!
                </h1>
              </div>
            </div>

            {hasMapAddress && (
              <div className="success-map-card">
                <div className="success-map-wrap">
                  <iframe
                    title="Shipping map"
                    src={mapSrc}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="success-map-iframe"
                  />
                  <div className="success-map-badge">
                    <div className="success-map-title">Shipping address</div>
                    <div className="success-map-text">
                      {shippingLocationLine || shippingAddressLine}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="success-confirm-box">
              <h3>
                {isPaid
                  ? "Your order is confirmed"
                  : "Your order is being processed"}
              </h3>

              <p>
                You will receive shipping updates by email.
              </p>
            </div>

            {/* ORDER DETAILS */}

            <div className="success-details-card">

              <h3>Order details</h3>

              <div className="success-details-grid">

                <div>
                  <h4>Contact</h4>
                  <p>{order?.customer?.email_used}</p>

                  <h4>Shipping address</h4>
                  <p>{shipping?.name}</p>
                  <p>{shipping?.line1}</p>
                  <p>{shipping?.line2}</p>
                  <p>{shipping?.city}, {shipping?.state}</p>
                  <p>{shipping?.pincode} {shipping?.country}</p>
                  <p>{shipping?.phone}</p>

                  <h4>Delivery</h4>
                  <p>Standard Delivery</p>
                  <p>Within 1 week</p>
                </div>

                <div>
                  <h4>Billing address</h4>
                  <p>{billing?.name}</p>
                  <p>{billing?.line1}</p>
                  <p>{billing?.city}, {billing?.state}</p>
                  <p>{billing?.pincode}</p>

                  <h4>Payment status</h4>
                  <p>{paymentStatus}</p>

                  <h4>Order date</h4>
                  <p>
                    {order?.created_at
                      ? new Date(order.created_at).toLocaleString()
                      : "-"}
                  </p>
                </div>

              </div>

            </div>

          </div>

          {/* RIGHT SIDE */}

          <div className="success-right">

            <div className="success-summary-card">

              {items.map((item) => (
                <div className="success-product-row" key={item.id}>

                  <div className="success-product-image-wrap">
                    <img
                      src={getProductImage(item)}
                      alt={item.name}
                      onError={(e) => { e.target.src = "/no-image.png"; }}
                    />
                    <span>{item.quantity}</span>
                  </div>

                  <div className="success-product-info">
                    <h4>{item.name}</h4>
                    <p>
                      {item.size ? `Size: ${item.size}` : ""}
                      {item.sku ? ` | SKU: ${item.sku}` : ""}
                    </p>
                  </div>

                  <div className="success-product-price">
                    {formatMoney(item.unit_price)}
                  </div>

                </div>
              ))}

              {/* PRICE */}

              <div className="success-price-line">
                <span>Subtotal</span>
                <strong>{formatMoney(subtotal)}</strong>
              </div>

              {discountValue > 0 && (
                <div className="success-price-line">
                  <span>Discount</span>
                  <strong>-{formatMoney(discountValue)}</strong>
                </div>
              )}

              <div className="success-price-line">
                <span>Shipping</span>
                <strong>
                  {shippingValue > 0
                    ? formatMoney(shippingValue)
                    : "FREE"}
                </strong>
              </div>

              <div className="success-total-row">
                <h4>Total</h4>
                <strong>{formatMoney(amountCharged)}</strong>
              </div>

              <div className="success-top-actions">
                <button
                  className="success-action-btn dark"
                  onClick={() => navigate("/profile/orders")}
                >
                  View my orders
                </button>

                <button
                  className="success-action-btn light"
                  onClick={() => navigate("/products")}
                >
                  Continue shopping
                </button>
              </div>

            </div>

          </div>

        </div>
      </div>

      <Footer />
    </>
  );
};

export default OrderSuccessPage;